import { useState, useCallback, useEffect } from 'react';
import { format, startOfTomorrow, getYear } from 'date-fns';
import { Calendar as CalendarIcon, AlertCircle } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { VariableType } from '@/lib/calculator';
import { cn } from '@/lib/utils';
import { useCalculatorStore } from '@/store/calculator';
import "react-datepicker/dist/react-datepicker.css";

interface VariableInputProps {
  type: VariableType | 'number';
  value: number;
  label?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
  // Add new prop to specifically identify calculation objective
  isCalculationObjective?: boolean;
  inputClassName?: string;
  min?: number;
  max?: number;
}

const years = Array.from({ length: 31 }, (_, i) => getYear(new Date()) + i);
const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export function VariableInput({ 
  type, 
  value, 
  label, 
  onChange, 
  disabled = false, 
  isCalculationObjective = false,
  inputClassName, 
  min, 
  max 
}: VariableInputProps) {
  const { values: storeValues } = useCalculatorStore();
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [localValue, setLocalValue] = useState(value);

  // Keep local state in sync with prop value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Memoize warning message function to prevent recreation on every render
  const getWarningMessage = useCallback((type: VariableType | 'number', value: number): string | null => {
    if (type === 'interest' && (value < 1 || value > 25)) {
      return 'Interest rate should be between 1% and 25%';
    }
    if (type === 'number' && (value < 5 || value > 50)) {
      return 'Mortgage term should be between 5 and 50 years';
    }
    if (type === 'price' && (value < 100000 || value > 9900000)) {
      return 'Purchase price should be between $100,000 and $9,900,000';
    }
    if (type === 'income') {
      if (value < 10000) {
        return 'Annual income should be at least $10,000';
      }
      if (value > 9900000) {
        return 'Annual income cannot exceed $9,900,000';
      }
    }
    if (type === 'downPayment' && max && value > max) {
      return `Down payment cannot exceed $${max.toLocaleString()}`;
    }
    if (type === 'savings' && max && value > max) {
      return `Monthly savings cannot exceed $${max.toLocaleString()}`;
    }
    return null;
  }, [max]);

  // Fixed formatValue function with better error handling
  const formatValue = useCallback((type: VariableType | 'number', val: number): string => {
    // Check if this is the calculation objective
    if (isCalculationObjective) {
      return "See results";
    }
    
    // Guard against NaN or undefined values
    if (val === null || val === undefined || isNaN(val)) {
      return type === 'interest' ? '0.00' : '0';
    }
    
    try {
      switch (type) {
        case 'price':
        case 'income':
        case 'savings':
        case 'downPayment':
        case 'number':
          return new Intl.NumberFormat('en-US', {
            style: 'decimal',
            maximumFractionDigits: 0,
          }).format(val);
        case 'interest':
          return isFocused ? inputValue : val.toFixed(2);
        case 'date':
          try {
            // Validate date value
            const dateObj = new Date(val);
            if (isNaN(dateObj.getTime())) {
              return format(new Date(), 'MMMM d, yyyy');
            }
            
            // Create a local date from UTC timestamp to avoid timezone shifts
            const utcDate = new Date(val);
            const localDate = new Date(
              utcDate.getUTCFullYear(),
              utcDate.getUTCMonth(),
              utcDate.getUTCDate()
            );
            return format(localDate, 'MMMM d, yyyy');
          } catch (dateError) {
            return format(new Date(), 'MMMM d, yyyy');
          }
        default:
          return val.toString();
      }
    } catch (error) {
      return '0';
    }
  }, [isFocused, inputValue, isCalculationObjective]);

  // Fixed parseValue function with safeguards
  const parseValue = useCallback((type: VariableType | 'number', inputValue: string): number => {
    if (!inputValue || inputValue.trim() === '') {
      return 0;
    }
    
    try {
      switch (type) {
        case 'price':
        case 'income':
        case 'savings':
        case 'downPayment':
        case 'number': {
          // Remove non-numeric characters and parse
          const cleanValue = inputValue.replace(/[^0-9.-]/g, '');
          
          // Safety check for empty string after cleaning
          if (!cleanValue) {
            setErrorMessage('');
            return 0;
          }
          
          const parsed = parseInt(cleanValue, 10);
          
          // Handle invalid parsing results
          if (isNaN(parsed) || !isFinite(parsed)) {
            setErrorMessage('Please enter a valid number');
            return 0;
          }
          
          // Clear error if valid
          setErrorMessage('');
          return parsed;
        }
        case 'interest': {
          // Handle empty string
          if (!inputValue.trim()) {
            setErrorMessage('');
            return 0;
          }
          
          const parsed = parseFloat(inputValue);
          if (isNaN(parsed) || !isFinite(parsed)) {
            setErrorMessage('Please enter a valid interest rate');
            return 0;
          }
          setErrorMessage('');
          return parsed;
        }
        case 'date': {
          // Parse date string and create UTC timestamp
          const date = new Date(inputValue);
          if (isNaN(date.getTime())) {
            setErrorMessage('Please enter a valid date');
            return Date.now();
          }
          setErrorMessage('');
          
          // Convert to UTC to avoid timezone issues
          const year = date.getFullYear();
          const month = date.getMonth();
          const day = date.getDate();
          return new Date(Date.UTC(year, month, day)).getTime();
        }
        default:
          return 0;
      }
    } catch (error) {
      setErrorMessage('An error occurred while processing the input');
      return 0;
    }
  }, []);

  // Safeguarded enforceMinimumValue function
  const enforceMinimumValue = useCallback((type: VariableType | 'number', val: number): number => {
    // Guard against NaN
    if (val === null || val === undefined || isNaN(val)) {
      return type === 'interest' ? 1 : type === 'income' ? 10000 : 0;
    }
    
    try {
      if (min !== undefined) {
        val = Math.max(min, val);
      }
      
      if (max !== undefined) {
        val = Math.min(max, val);
      }
      
      if (type === 'interest') {
        return Math.max(1, Math.min(25, val));
      }
      if (type === 'number') {
        return Math.max(5, Math.min(50, val));
      }
      if (type === 'price') {
        return Math.max(100000, Math.min(9900000, val));
      }
      if (type === 'income') {
        return Math.max(10000, Math.min(9900000, val));
      }
      if (type === 'downPayment') {
        return Math.min(max || 9900000, val);
      }
      if (type === 'savings') {
        return Math.min(max || 999999, val);
      }
      if (type === 'date') {
        const tomorrow = startOfTomorrow().getTime();
        if (val < tomorrow) {
          // Convert tomorrow to UTC for consistent comparison
          const tomorrowDate = startOfTomorrow();
          const year = tomorrowDate.getFullYear();
          const month = tomorrowDate.getMonth();
          const day = tomorrowDate.getDate();
          return new Date(Date.UTC(year, month, day)).getTime();
        }
      }
      return val;
    } catch (error) {
      return 0;
    }
  }, [min, max]);

  const handleDateChange = useCallback((date: Date | null) => {
    if (!date || disabled) return;
    
    // Handle "Clear" button (date is null)
    if (date === null) {
      // Reset to the app's default date (first of next month)
      const defaultDate = storeValues.date.value;
      onChange(defaultDate);
      return;
    }
    
    // Use UTC to avoid timezone issues - same approach as embed pages
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const selectedTime = new Date(Date.UTC(year, month, day)).getTime();
    
    const correctedValue = enforceMinimumValue('date', selectedTime);
    onChange(correctedValue);
  }, [disabled, enforceMinimumValue, onChange, storeValues.date.value]);

  // Fixed handleValueChange to prevent memory leaks when backspacing
  const handleValueChange = useCallback((newInputValue: string) => {
    if (disabled) return;

    try {
      // Handle empty input first - separate case to avoid excess processing
      if (!newInputValue || newInputValue.trim() === '') {
        if (type === 'interest') {
          setInputValue('');
        }
        
        setLocalValue(0);
        setShowWarning(true);
        return;
      }

      if (type === 'interest') {
        // Allow empty input or numbers with up to two decimal points
        if (/^\d*\.?\d*$/.test(newInputValue)) {
          setInputValue(newInputValue);
          const parsed = parseFloat(newInputValue);
          if (!isNaN(parsed)) {
            setLocalValue(parsed);
            setShowWarning(parsed < 1 || parsed > 25);
          }
        }
      } else {
        // Handle non-interest types
        const newValue = parseValue(type, newInputValue);
        setLocalValue(newValue);
        
        if (type === 'number') {
          setShowWarning(newValue < 5 || newValue > 50);
        } else if (type === 'price') {
          setShowWarning(newValue < 100000 || newValue > 9900000);
        } else if (type === 'income') {
          setShowWarning(newValue < 10000 || newValue > 9900000);
        } else if (type === 'downPayment') {
          setShowWarning(max !== undefined && newValue > max);
        } else if (type === 'savings') {
          setShowWarning(max !== undefined && newValue > max);
        }
      }
    } catch (error) {
      setErrorMessage('An error occurred while processing your input');
    }
  }, [disabled, type, parseValue, max]);

  // Use current value from props when focusing
  const handleFocus = useCallback(() => {
    if (disabled) return;
    setIsFocused(true);
    // Always reset localValue to the current prop value when focusing
    setLocalValue(value);
    if (type === 'interest') {
      setInputValue(value.toString());
    }
    // Clear any existing error messages on focus
    setErrorMessage('');
  }, [disabled, type, value]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    try {
      // For income field, always ensure a minimum value
      if (type === 'income' && (localValue === 0 || localValue < 10000)) {
        const correctedValue = enforceMinimumValue(type, 10000);
        onChange(correctedValue);
        setShowWarning(false);
        return;
      }
      
      // Enforce minimum values and trigger onChange
      const correctedValue = enforceMinimumValue(type, localValue);
      if (correctedValue !== localValue) {
        setLocalValue(correctedValue); // Update local state to match corrected value
        onChange(correctedValue);
      } else {
        onChange(localValue);
      }

      setShowWarning(false);
    } catch (error) {
      setErrorMessage('An error occurred while validating the input');
    }
  }, [enforceMinimumValue, onChange, type, localValue]);

  const warningMsg = getWarningMessage(type, localValue);
  const tomorrow = startOfTomorrow();

  // Return the UI
  return (
    <div className="w-full">
      {label && (
        <label
          className={cn(
            'block text-sm mb-0.5 transition-colors',
            disabled ? 'text-gray-400' : isFocused ? 'text-primary' : 'text-gray-600'
          )}>
          {label}
        </label>
      )}
      <div
        className={cn(
          'flex items-center bg-white border-b-2 transition-colors',
          disabled ? 'border-gray-200 bg-gray-50' : isFocused ? 'border-primary' : 'border-gray-200',
          errorMessage ? 'border-red-500' : ''
        )}>
        {type === 'date' ? (
          <DatePicker
            selected={(() => {
              // Create a local date from UTC timestamp to avoid timezone shifts
              const utcDate = new Date(value);
              return new Date(
                utcDate.getUTCFullYear(),
                utcDate.getUTCMonth(),
                utcDate.getUTCDate()
              );
            })()}
            onChange={handleDateChange}
            minDate={tomorrow}
            maxDate={new Date(2050, 11, 31)}
            dateFormat="MMMM d, yyyy"
            disabled={disabled}
            isClearable={false}
            todayButton=""
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={30}
            showMonthDropdown
            dropdownMode="select"
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div className="flex items-center justify-between px-3 py-2">
                <button
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  type="button"
                  className={cn(
                    "p-1 rounded-full hover:bg-gray-100",
                    prevMonthButtonDisabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                    />
                  </svg>
                </button>

                <div className="flex items-center gap-2">
                  <select
                    value={getYear(date)}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                    className="text-sm bg-transparent border border-gray-200 rounded px-2 py-1 appearance-none cursor-pointer hover:border-primary focus:border-primary focus:outline-none"
                  >
                    {years.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <select
                    value={months[date.getMonth()]}
                    onChange={({ target: { value } }) =>
                      changeMonth(months.indexOf(value))
                    }
                    className="text-sm bg-transparent border border-gray-200 rounded px-2 py-1 appearance-none cursor-pointer hover:border-primary focus:border-primary focus:outline-none"
                  >
                    {months.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                  type="button"
                  className={cn(
                    "p-1 rounded-full hover:bg-gray-100",
                    nextMonthButtonDisabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"
                    />
                  </svg>
                </button>
              </div>
            )}
            className={cn(
              'w-full py-2.5 px-4 text-gray-900 bg-transparent focus:outline-none',
              inputClassName, // Apply custom font size
              disabled ? 'cursor-not-allowed text-gray-500' : ''
            )}
            customInput={
              <div className="flex items-center justify-between w-full cursor-pointer">
                <span className={inputClassName}>{formatValue(type, value)}</span>
                <CalendarIcon className={cn(
                  'w-5 h-5 ml-2 transition-colors flex-shrink-0',
                  disabled ? 'text-gray-400' : 'text-gray-400'
                )} />
              </div>
            }
          />
        ) : (
          <>
            {(type === 'price' || type === 'income' || type === 'savings' || type === 'downPayment') && (
              <span className={cn(
                'pl-4 transition-colors',
                disabled ? 'text-gray-400' : isFocused ? 'text-primary' : 'text-gray-400'
              )}>$</span>
            )}
            <input
              type="text"
              value={isFocused ? (type === 'interest' ? inputValue : formatValue(type, localValue)) : formatValue(type, value)}
              onChange={(e) => handleValueChange(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              className={cn(
                'w-full py-2.5 px-4 text-gray-900 bg-transparent focus:outline-none',
                inputClassName, // Apply custom font size
                disabled ? 'cursor-not-allowed text-gray-500' : ''
              )}
              aria-invalid={!!errorMessage}
              aria-describedby={errorMessage ? 'input-error' : undefined}
            />
            {type === 'interest' && (
              <span className={cn(
                'pr-4 transition-colors',
                disabled ? 'text-gray-400' : isFocused ? 'text-primary' : 'text-gray-400'
              )}>%</span>
            )}
          </>
        )}
      </div>
      {(showWarning && warningMsg) && (
        <div className="flex items-center gap-2 mt-2 text-sm text-amber-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{warningMsg}</span>
        </div>
      )}
      {errorMessage && (
        <div id="input-error" className="flex items-center gap-2 mt-2 text-sm text-red-500">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
}